import { formatearMoneda } from '@plataforma/utils';

import {
  actualizarCantidad,
  aplicarDecisionPostEnvio,
  aplicarCupon,
  calcularTotales,
  consumirCupon,
  crearUrlWhatsApp,
  leerCarrito,
  mensajeErrorCupon,
  obtenerCuponAplicado,
  obtenerLineasCarrito,
  quitarCupon,
  quitarProducto,
  vaciarCarrito,
} from '../carrito.js';
import { escaparHtml, imagenPrincipal } from '../componentes.js';
import { cargarDetalleProducto } from '../datos-publicados.js';
import { iconoLucide } from '../iconos.js';
import { iconoRedSocial } from '../redes-sociales.js';

async function tiendaConDetalleDeCarrito(tienda) {
  const slugsEnCarrito = [...new Set(leerCarrito().map((item) => item.slug))];
  if (!slugsEnCarrito.length) return tienda;

  const detalles = await Promise.all(
    slugsEnCarrito.map((slug) => cargarDetalleProducto(tienda.slugTienda, slug)),
  );
  const detallePorSlug = new Map(slugsEnCarrito.map((slug, indice) => [slug, detalles[indice]]));

  return {
    ...tienda,
    productos: tienda.productos.map((producto) =>
      detallePorSlug.get(producto.slug)
        ? { ...producto, ...detallePorSlug.get(producto.slug) }
        : producto,
    ),
  };
}

function renderizarCarrito(contenedor, tienda) {
  const lineas = obtenerLineasCarrito(tienda);
  const planNombre = String(tienda?.empresa?.plan_nombre ?? 'Prueba');
  const muestraCupon = planNombre === 'Medio' || planNombre === 'Pro';

  if (!muestraCupon && obtenerCuponAplicado()) {
    quitarCupon();
  }

  const { subtotal, descuento, total } = calcularTotales(lineas, muestraCupon);
  const cupon = obtenerCuponAplicado();

  contenedor.innerHTML = `
    <section class="sp-page sp-contenedor">
      <div class="sp-page__cabecera">
        <p class="sp-eyebrow">Carrito</p>
        <h1>Tu selección</h1>
        <p>Revisa los productos, agrega tus datos y envía el pedido por WhatsApp.</p>
      </div>

      ${
        lineas.length
          ? `<form class="sp-carrito" id="sp-form-carrito">
              <div class="sp-carrito__lineas">
                ${lineas
                  .map((linea) => {
                    const imagen = imagenPrincipal(linea.producto);
                    return `
                      <article class="sp-linea-carrito">
                        <img src="${escaparHtml(imagen.url)}" alt="${escaparHtml(linea.producto.nombre)}" loading="lazy" decoding="async" />
                        <div>
                          <h2>${escaparHtml(linea.producto.nombre)}</h2>
                          ${linea.varianteTexto ? `<p>${escaparHtml(linea.varianteTexto)}</p>` : ''}
                          <p>${formatearMoneda(Number(linea.precio), tienda.empresa.moneda)} c/u</p>
                        </div>
                        <div class="sp-cantidad" aria-label="Cantidad de ${escaparHtml(linea.producto.nombre)}">
                          <button type="button" data-restar="${escaparHtml(linea.itemKey)}" aria-label="Restar ${escaparHtml(linea.producto.nombre)}">${iconoLucide('Minus')}</button>
                          <input type="number" min="1" max="99" value="${linea.cantidad}" data-cantidad="${escaparHtml(linea.itemKey)}" aria-label="Cantidad" />
                          <button type="button" data-sumar="${escaparHtml(linea.itemKey)}" aria-label="Sumar ${escaparHtml(linea.producto.nombre)}">${iconoLucide('Plus')}</button>
                        </div>
                        <strong>${formatearMoneda(linea.subtotal, tienda.empresa.moneda)}</strong>
                        <button class="sp-linea-carrito__quitar" type="button" data-quitar="${escaparHtml(linea.itemKey)}" aria-label="Quitar ${escaparHtml(linea.producto.nombre)}">${iconoLucide('Trash2')}<span>Quitar</span></button>
                      </article>
                    `;
                  })
                  .join('')}
              </div>

              <aside class="sp-resumen-carrito">
                ${
                  muestraCupon
                    ? `
                <div class="sp-cupon">
                  ${
                    cupon
                      ? `
                      <div class="sp-cupon__aplicado">
                        <div class="sp-cupon__aplicado-info">
                          <span class="sp-cupon__codigo">${iconoLucide('Check')}${escaparHtml(cupon.codigo)}</span>
                          <span class="sp-cupon__descuento">Descuento aplicado: -${formatearMoneda(cupon.descuento, tienda.empresa.moneda)}</span>
                        </div>
                        <button type="button" class="sp-cupon__quitar sp-cupon__quitar--texto" id="sp-quitar-cupon" aria-label="Quitar cupón">
                          ${iconoLucide('X')}<span>Quitar</span>
                        </button>
                      </div>
                    `
                      : `
                      <div class="sp-cupon__form">
                        <label for="sp-codigo-cupon" class="sp-cupon__label">¿Tienes un cupón?</label>
                        <div class="sp-cupon__fila">
                          <input type="text" id="sp-codigo-cupon" placeholder="Código de descuento" autocomplete="off" />
                          <button type="button" class="sp-boton sp-boton--secundario" id="sp-aplicar-cupon">Aplicar</button>
                        </div>
                      </div>
                    `
                  }
                </div>
                `
                    : ''
                }

                <div class="sp-resumen-carrito__totales">
                  <div class="sp-resumen-carrito__fila">
                    <span>Subtotal</span>
                    <strong>${formatearMoneda(subtotal, tienda.empresa.moneda)}</strong>
                  </div>
                  ${
                    descuento > 0
                      ? `
                    <div class="sp-resumen-carrito__fila sp-resumen-carrito__fila--descuento">
                      <span>Descuento</span>
                      <strong>-${formatearMoneda(descuento, tienda.empresa.moneda)}</strong>
                    </div>
                  `
                      : ''
                  }
                  <div class="sp-resumen-carrito__fila sp-resumen-carrito__fila--total">
                    <span>Total</span>
                    <strong>${formatearMoneda(total, tienda.empresa.moneda)}</strong>
                  </div>
                </div>

                <p class="sp-resumen-carrito__aviso">El precio final puede variar según disponibilidad, envío o coordinación directa con la tienda.</p>

                <div class="sp-carrito__datos-cliente">
                  <p class="sp-carrito__datos-titulo">Tus datos</p>
                  <label>
                    <span>Nombre</span>
                    <input type="text" id="sp-cliente-nombre" placeholder="Tu nombre" autocomplete="name" required />
                  </label>
                  <label>
                    <span>Teléfono</span>
                    <input type="tel" id="sp-cliente-telefono" placeholder="Tu teléfono" autocomplete="tel" required />
                    <small class="sp-campo-ayuda">Lo usaremos para validar el límite de uso del cupón.</small>
                  </label>
                  <label>
                    <span>Distrito / Dirección</span>
                    <input type="text" id="sp-cliente-distrito" placeholder="Distrito o dirección de envío" autocomplete="address-line2" />
                  </label>
                  <label>
                    <span>Observaciones</span>
                    <textarea id="sp-cliente-observaciones" rows="2" placeholder="Observaciones opcionales"></textarea>
                  </label>
                </div>

                <button class="sp-boton sp-boton--primario sp-boton--envio" type="submit">
                  ${iconoRedSocial('whatsapp')}<span>Enviar por WhatsApp</span>
                </button>
                <button class="sp-boton sp-boton--secundario" type="button" id="sp-vaciar-carrito">${iconoLucide('Trash2')}<span>Vaciar carrito</span></button>
              </aside>
            </form>`
          : `<div class="sp-vacio">
              <span class="sp-vacio__icono" aria-hidden="true">${iconoLucide('ShoppingBag')}</span>
              <h2>Tu carrito está vacío</h2>
              <p>Agrega productos del catálogo para armar tu consulta.</p>
              <a class="sp-boton sp-boton--primario" href="/catalogo">${iconoLucide('ShoppingBag')}<span>Ver catálogo</span></a>
            </div>`
      }
    </section>
  `;

  contenedor.querySelectorAll('[data-cantidad]').forEach((input) => {
    input.addEventListener('change', () => {
      actualizarCantidad(input.dataset.cantidad, Math.max(1, Number(input.value) || 1));
      renderizarCarrito(contenedor, tienda);
    });
  });

  contenedor.querySelectorAll('[data-quitar]').forEach((boton) => {
    boton.addEventListener('click', () => {
      quitarProducto(boton.dataset.quitar);
      renderizarCarrito(contenedor, tienda);
    });
  });

  contenedor.querySelectorAll('[data-restar]').forEach((boton) => {
    boton.addEventListener('click', () => {
      const linea = obtenerLineasCarrito(tienda).find(
        (item) => item.itemKey === boton.dataset.restar,
      );
      actualizarCantidad(boton.dataset.restar, Math.max(1, Number(linea?.cantidad ?? 1) - 1));
      renderizarCarrito(contenedor, tienda);
    });
  });

  contenedor.querySelectorAll('[data-sumar]').forEach((boton) => {
    boton.addEventListener('click', () => {
      const linea = obtenerLineasCarrito(tienda).find(
        (item) => item.itemKey === boton.dataset.sumar,
      );
      actualizarCantidad(boton.dataset.sumar, Math.min(99, Number(linea?.cantidad ?? 1) + 1));
      renderizarCarrito(contenedor, tienda);
    });
  });

  contenedor.querySelector('#sp-vaciar-carrito')?.addEventListener('click', () => {
    vaciarCarrito();
    renderizarCarrito(contenedor, tienda);
  });

  contenedor.querySelector('#sp-aplicar-cupon')?.addEventListener('click', async () => {
    const input = contenedor.querySelector('#sp-codigo-cupon');
    const codigo = input?.value.trim();
    const telefono = contenedor.querySelector('#sp-cliente-telefono')?.value.trim() || '';

    if (!codigo) {
      alert('Ingresa un código de cupón.');
      return;
    }

    const resultado = await aplicarCupon(tienda, codigo, telefono);
    if (resultado.error) {
      alert(mensajeErrorCupon(resultado.error));
      return;
    }

    renderizarCarrito(contenedor, tienda);
  });

  contenedor.querySelector('#sp-quitar-cupon')?.addEventListener('click', () => {
    quitarCupon();
    renderizarCarrito(contenedor, tienda);
  });

  contenedor.querySelector('#sp-form-carrito')?.addEventListener('submit', async (evento) => {
    evento.preventDefault();

    const cupon = obtenerCuponAplicado();
    const datosCliente = {
      nombre: contenedor.querySelector('#sp-cliente-nombre')?.value.trim(),
      telefono: contenedor.querySelector('#sp-cliente-telefono')?.value.trim(),
      distrito: contenedor.querySelector('#sp-cliente-distrito')?.value.trim(),
      observaciones: contenedor.querySelector('#sp-cliente-observaciones')?.value.trim(),
    };

    if (cupon) {
      const resultadoConsumo = await consumirCupon(tienda, datosCliente.telefono || '');
      if (resultadoConsumo.error) {
        alert(mensajeErrorCupon(resultadoConsumo.error));
        return;
      }
    }

    const urlWhatsApp = crearUrlWhatsApp(tienda, obtenerLineasCarrito(tienda), datosCliente, cupon);

    if (!urlWhatsApp) {
      window.location.href = '/contacto';
      return;
    }

    window.open(urlWhatsApp, '_blank', 'noopener,noreferrer');
    const debeVaciar = window.confirm('Pedido enviado a WhatsApp. ¿Deseas vaciar el carrito?');
    aplicarDecisionPostEnvio(debeVaciar ? 'vaciar' : 'conservar');
    renderizarCarrito(contenedor, tienda);
  });
}

export function vistaCarrito(contenedor, tienda) {
  contenedor.dataset.vistaActual = 'carrito';
  renderizarCarrito(contenedor, tienda);

  if (!tienda._carritoDetalleCompleto) {
    tiendaConDetalleDeCarrito(tienda).then((tiendaEnriquecida) => {
      if (contenedor.dataset.vistaActual !== 'carrito') return;
      vistaCarrito(contenedor, { ...tiendaEnriquecida, _carritoDetalleCompleto: true });
    });
  }
}
